import { Box, Grid2 as Grid, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { API_TO_CTX_CAT, CTX_CATEGORY_LABELS } from './ContextScoreView._mock';

import type {
  HauserEQAirQualityDetails,
  HauserEQAmenityDetails,
  HauserEQCrimeComponentDetails,
  HauserEQCrimeOverallDetails,
  HauserEQGenericSubfactorInput,
  HauserEQGenericSubfactorDetails,
  HauserEQLocationDetails,
  HauserEQNoiseExposureDetails,
  HauserEQRadonMoldDetails,
  HauserEQWaterQualityDetails,
} from '@/services/api/hauser-eq/api';

import {
  COLORS,
  CTX_RADAR_TOOLTIPS,
  isHiddenSubscore,
  NeighborhoodMapCard,
  NHDDisclosuresCard,
  pickHeroFinding,
  ScrollHintList,
  SubfactorRow,
  SubscoreDetailCard,
  Tip,
} from '@/components/dashboard/scoring';
import ContextScoreCard from '@/components/home/cards/ContextScoreCard';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useNHDFindings } from '@/hooks/useNHDFindings';
import { useScoringConfig } from '@/hooks/useScoringConfig';
import { VALUE_FONT_SMALL } from '@/theme/consts';

// Default center (Carlsbad, CA area as shown in design)
const DEFAULT_CENTER = { latitude: 33.16, longitude: -117.35 };

const CONTEXT_CATEGORY_KEYS = [
  'NATURAL_HAZARDS',
  'HEALTH_QUALITY',
  'SAFETY',
  'LOCATION',
  'AMENITIES',
  'SITE',
];

const CTX_SUBSCORE_QUESTIONS: Record<string, string> = {
  LocationQuality: 'Is this a good area to live in?',
  RiskExposure: 'How safe is this property and the area around it?',
};

const NOISE_MODE_LABELS: Record<string, string> = {
  road: 'Road',
  aviation: 'Aviation',
  rail: 'Rail',
};

const NOISE_MODE_ORDER = ['road', 'aviation', 'rail'] as const;

const EXPOSURE_RANK: Record<string, number> = {
  'Very Low': 1,
  Low: 2,
  Moderate: 3,
  High: 4,
  'Very High': 5,
};

const lowerRating = (rating?: string) => rating?.toLocaleLowerCase();

const AirQualityDetails = ({
  details,
  narrative,
}: {
  details: HauserEQAirQualityDetails;
  narrative?: string;
}) => {
  const items = [
    details.overall && {
      key: 'overall',
      label: 'Overall',
      value:
        details.overall.rating ??
        (details.overall.grade ? `Grade ${details.overall.grade}` : 'Not available'),
      context:
        details.overall.national_percentile == null
          ? undefined
          : `${details.overall.national_percentile}%`,
      contextTooltip:
        details.overall.national_percentile == null
          ? undefined
          : `Overall exposure is higher than ${details.overall.national_percentile}% of locations nationwide.`,
    },
    details.components?.ozone && {
      key: 'ozone',
      label: 'Ozone',
      value:
        details.components.ozone.rating ??
        (details.components.ozone.grade
          ? `Grade ${details.components.ozone.grade}`
          : 'Not available'),
    },
    details.components?.fine_particulate && {
      key: 'fine_particulate',
      label: 'PM2.5',
      value:
        details.components.fine_particulate.rating ??
        (details.components.fine_particulate.grade
          ? `Grade ${details.components.fine_particulate.grade}`
          : 'Not available'),
    },
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));

  if (items.length === 0) return null;

  return (
    <div data-testid="air-quality-details" style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid="air-quality-blurb"
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 8,
          marginTop: 8,
        }}>
        {items.map((item) => (
          <div
            key={item.key}
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, minWidth: 0 }}>
            <div
              style={{
                color: 'var(--tx-muted)',
                fontSize: 11,
              }}>
              {item.label}
            </div>
            <div style={{ color: 'var(--tx-text)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
              {item.value}
              {item.context && (
                <Tooltip title={item.contextTooltip ?? ''} arrow>
                  <span style={{ color: COLORS.textDim, cursor: 'help' }}>
                    {' · '}
                    {item.context}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WATER_COMPONENT_LABELS = {
  pfas: 'PFAS',
  hardness: 'Water Hardness',
  groundwater_arsenic: 'Groundwater Arsenic',
} as const;

const WaterQualityDetails = ({
  details,
  narrative,
}: {
  details: HauserEQWaterQualityDetails;
  narrative?: string;
}) => {
  const items = Object.entries(WATER_COMPONENT_LABELS).flatMap(([key, label]) => {
    const component = details.components[key as keyof typeof details.components];
    return component ? [{ key, label, component }] : [];
  });
  if (items.length === 0) return null;

  return (
    <div data-testid="water-quality-details" style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid="water-quality-blurb"
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 8,
          marginTop: 8,
        }}>
        {items.map(({ key, label, component }) => (
          <div
            key={key}
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, minWidth: 0 }}>
            <div
              style={{
                color: 'var(--tx-muted)',
                fontSize: 11,
              }}>
              {label}
            </div>
            <div style={{ color: 'var(--tx-text)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
              {component.rating ?? (component.grade ? `Grade ${component.grade}` : 'Not available')}
              {component.expected_range && (
                <span style={{ color: COLORS.textDim }}>
                  {' · '}
                  {component.expected_range.min}–{component.expected_range.max}{' '}
                  {component.expected_range.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RADON_MOLD_LABELS = { radon: 'Radon', mold: 'Mold' } as const;

const RadonMoldDetails = ({
  details,
  narrative,
}: {
  details: HauserEQRadonMoldDetails;
  narrative?: string;
}) => {
  const items = Object.entries(RADON_MOLD_LABELS).flatMap(([key, label]) => {
    const component = details.components[key as keyof typeof details.components];
    return component ? [{ key, label, component }] : [];
  });
  if (items.length === 0) return null;

  return (
    <div data-testid="radon-mold-details" style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid="radon-mold-blurb"
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 8,
          marginTop: 8,
        }}>
        {items.map(({ key, label, component }) => (
          <div
            key={key}
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, minWidth: 0 }}>
            <div
              style={{
                color: 'var(--tx-muted)',
                fontSize: 11,
              }}>
              {label}
            </div>
            <div style={{ color: 'var(--tx-text)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
              {component.rating ?? (component.grade ? `Grade ${component.grade}` : 'Not available')}
              {component.expected_range && (
                <span style={{ color: COLORS.textDim }}>
                  {' · '}
                  {component.expected_range.min}–{component.expected_range.max}{' '}
                  {component.expected_range.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CRIME_AREA_LABELS = {
  immediate: 'Immediate',
  half_mile: '½ Mile',
  one_mile: '1 Mile',
} as const;

const CRIME_COMPONENT_LABELS = {
  aggravated_assault: 'Assault',
  robbery: 'Robbery',
  murder: 'Murder',
  rape: 'Sexual Violence',
  burglary: 'Burglary',
  larceny: 'Larceny',
  motor_vehicle_theft: 'Vehicle Theft',
} as const;

const CrimeEvidenceGrid = ({
  items,
}: {
  items: Array<{ key: string; label: string; rating?: string; grade?: string }>;
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
      gap: 8,
      marginTop: 8,
    }}>
    {items.map((item) => (
      <div
        key={item.key}
        style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, minWidth: 0 }}>
        <div
          style={{
            color: 'var(--tx-muted)',
            fontSize: 11,
          }}>
          {item.label}
        </div>
        <div style={{ color: 'var(--tx-text)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
          {item.rating ?? (item.grade ? `Grade ${item.grade}` : 'Not available')}
        </div>
      </div>
    ))}
  </div>
);

const CrimeOverallDetails = ({
  details,
  narrative,
}: {
  details: HauserEQCrimeOverallDetails;
  narrative?: string;
}) => {
  const items = Object.entries(CRIME_AREA_LABELS).flatMap(([key, label]) => {
    const evidence = details.areas[key as keyof typeof details.areas];
    return evidence ? [{ key, label, ...evidence }] : [];
  });
  if (items.length === 0) return null;

  return (
    <div data-testid="crime-overall-details" style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid="crime-overall-blurb"
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      <CrimeEvidenceGrid items={items} />
    </div>
  );
};

const CrimeComponentDetails = ({
  details,
  narrative,
  subfactor,
}: {
  details: HauserEQCrimeComponentDetails;
  narrative?: string;
  subfactor: 'violent_crime' | 'property_crime';
}) => {
  const allowed =
    subfactor === 'violent_crime'
      ? (['aggravated_assault', 'robbery', 'murder', 'rape'] as const)
      : (['burglary', 'larceny', 'motor_vehicle_theft'] as const);
  const items = allowed.flatMap((key) => {
    const evidence = details.components[key];
    return evidence ? [{ key, label: CRIME_COMPONENT_LABELS[key], ...evidence }] : [];
  });
  if (items.length === 0) return null;

  return (
    <div data-testid={`${subfactor.replace('_', '-')}-details`} style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid={`${subfactor.replace('_', '-')}-blurb`}
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      <CrimeEvidenceGrid items={items} />
    </div>
  );
};

const LOCATION_SERVICE_LABELS: Record<string, string> = {
  fire_protection: 'Nearest Fire Station',
  law_enforcement: 'Nearest Law Enforcement',
  medical_response: 'Nearest Ambulance Service',
};

const formatDistance = (measurement?: { value: number; unit?: string }) => {
  if (!measurement) return undefined;
  const unit = measurement.unit?.toLocaleLowerCase();
  const suffix = unit === 'miles' || unit === 'mile' || unit === 'mi' ? 'mi' : measurement.unit;
  return `${Number(measurement.value.toFixed(2))}${suffix ? ` ${suffix}` : ''}`;
};

const formatDuration = (measurement?: { value: number; unit?: string }) => {
  if (!measurement) return undefined;
  const unit = measurement.unit?.toLocaleLowerCase();
  if (unit === 'seconds' || unit === 'second') {
    return `${Math.max(1, Math.round(measurement.value / 60))} min`;
  }
  return `${Number(measurement.value.toFixed(1))}${measurement.unit ? ` ${measurement.unit}` : ''}`;
};

const LocationDetails = ({
  details,
  narrative,
  subfactor,
}: {
  details: HauserEQLocationDetails;
  narrative?: string;
  subfactor:
    | 'walkability'
    | 'transit_access'
    | 'fire_protection'
    | 'law_enforcement'
    | 'medical_response';
}) => {
  const service = details.nearest_service;
  const drivingDistance = formatDistance(service?.driving_distance);
  const drivingDuration = formatDuration(service?.driving_duration);
  const items = [
    {
      key: 'rating',
      label: 'Area Rating',
      value: details.rating ?? (details.grade ? `Grade ${details.grade}` : undefined),
    },
    service?.name && {
      key: 'service',
      label: LOCATION_SERVICE_LABELS[subfactor] ?? 'Nearest Service',
      value: service.name,
    },
    service?.distance && {
      key: 'distance',
      label: 'Direct Distance',
      value: formatDistance(service.distance),
    },
    (drivingDistance || drivingDuration) && {
      key: 'driving',
      label: 'Drive to Station',
      value: [drivingDistance, drivingDuration].filter(Boolean).join(' · '),
    },
    service?.type && {
      key: 'type',
      label: 'Facility Type',
      value: service.type,
    },
    service?.volunteer != null && {
      key: 'staffing',
      label: 'Station Staffing',
      value: service.volunteer ? 'Volunteer' : 'Career / non-volunteer',
    },
  ].filter(
    (item): item is { key: string; label: string; value: string } =>
      typeof item === 'object' && item !== null && Boolean(item.value),
  );

  if (!narrative && items.length === 0) return null;

  return (
    <div data-testid={`location-details-${subfactor}`} style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid={`location-blurb-${subfactor}`}
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      {items.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: 8,
            marginTop: 8,
          }}>
          {items.map((item) => (
            <div
              key={item.key}
              style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, minWidth: 0 }}>
              <div
                style={{
                  color: 'var(--tx-muted)',
                  fontSize: 11,
                }}>
                {item.label}
              </div>
              <div
                style={{
                  color: COLORS.text,
                  fontSize: 12,
                  marginTop: 3,
                  overflowWrap: 'anywhere',
                }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AmenityDetails = ({
  details,
  narrative,
  subfactor,
}: {
  details: HauserEQAmenityDetails;
  narrative?: string;
  subfactor: string;
}) => {
  const sources = Object.entries(details.sources);
  if (!narrative && sources.length === 0) return null;

  return (
    <div data-testid={`amenity-details-${subfactor}`} style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid={`amenity-blurb-${subfactor}`}
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      {sources.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8,
            marginTop: 8,
          }}>
          {sources.map(([key, source]) => {
            const nearest = source.nearest;
            const context = [
              nearest && `${Number(nearest.distance_miles.toFixed(2))} mi nearest`,
              `${source.count_within_radius} within ${source.coverage_radius_miles} mi`,
            ]
              .filter(Boolean)
              .join(' · ');
            const descriptor = [nearest?.school_level, nearest?.type].filter(Boolean).join(' · ');
            return (
              <div
                key={key}
                style={{
                  borderTop: '1px solid var(--tx-line)',
                  paddingTop: 8,
                  minWidth: 0,
                }}>
                <div
                  style={{
                    color: 'var(--tx-muted)',
                    fontSize: 11,
                  }}>
                  {source.label}
                </div>
                <div
                  style={{
                    color: COLORS.text,
                    fontSize: 12,
                    marginTop: 3,
                    overflowWrap: 'anywhere',
                  }}>
                  {nearest?.name ?? 'No nearby result'}
                </div>
                <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 2 }}>
                  {context}
                  {descriptor ? ` · ${descriptor}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const GENERIC_INPUT_LABELS: Record<string, string> = {
  fema_flood: 'FEMA Flood',
  riverine_flooding: 'Riverine Flooding',
  coastal_flooding: 'Coastal Flooding',
  tsunami: 'Tsunami',
  wildfire: 'Wildfire',
  earthquake: 'Earthquake',
  tornado: 'Tornado',
  strong_wind: 'Strong Wind',
  hurricane: 'Hurricane',
  winter_weather: 'Winter Weather',
  cold_wave: 'Cold Wave',
  ice_storm: 'Ice Storm',
  snow_load: 'Snow Load',
  avalanche: 'Avalanche',
  frozen_pipes: 'Frozen Pipes',
  ice_dam: 'Ice Dam',
  sinkhole: 'Sinkhole',
  landslide: 'Landslide',
  lightning: 'Lightning',
  hail: 'Hail',
  heatwave: 'Heatwave',
  volcanic_activity: 'Volcanic Activity',
  termite_infestation: 'Termite Activity',
  superfund_sites: 'Superfund Sites',
  brownfields: 'Brownfields',
  toxic_release_facilities: 'Toxic Release Facilities',
  underground_storage_tanks: 'Underground Storage Tanks',
  asbestos_facilities: 'Asbestos Facilities',
  damaging_events: 'Damaging Events',
  overall: 'Overall Crime',
  murder: 'Murder',
  rape: 'Rape',
  aggravated_assault: 'Aggravated Assault',
  robbery: 'Robbery',
  burglary: 'Burglary',
  larceny: 'Larceny',
  motor_vehicle_theft: 'Vehicle Theft',
  crime_expanded_range_half_mile: 'Broader Area',
  walkability: 'Walkability',
  public_transit: 'Public Transit',
  fire_protection_response: 'Fire Response',
  law_enforcement_response: 'Law Enforcement Response',
  medical_response: 'Medical Response',
  schools: 'Nearby Schools',
  size: 'Lot Size',
  elevation: 'Elevation',
  sewer: 'Sewer Service',
  water: 'Water Service',
  adopted_international_building_code: 'International Building Code',
  adopted_international_residential_code: 'International Residential Code',
};

const RISK_SUBFACTORS = new Set([
  'flood_risk',
  'fire_risk',
  'seismic_risk',
  'wind_storm_risk',
  'winter_weather',
  'soil_subsidence',
  'other_hazards',
  'contamination_proximity',
  'recent_disaster_history',
  'overall_crime',
  'violent_crime',
  'property_crime',
]);

const riskLabelFromScore = (score: number) => {
  if (score >= 86) return 'very low';
  if (score >= 77) return 'low';
  if (score >= 68) return 'moderate';
  if (score >= 59) return 'high';
  return 'very high';
};

const qualityLabelFromScore = (score: number) => {
  if (score >= 86) return 'very favorable';
  if (score >= 77) return 'favorable';
  if (score >= 68) return 'typical';
  if (score >= 59) return 'limited';
  return 'not favorable';
};

const formatGenericValue = (value: string | number | boolean, unit?: string) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const formatted = typeof value === 'number' ? value.toLocaleString() : value;
  return `${formatted}${unit ? ` ${unit}` : ''}`;
};

const labelForInput = (input: HauserEQGenericSubfactorInput) =>
  GENERIC_INPUT_LABELS[input.key] ?? input.key.replace(/_/g, ' ');

const nearestInput = (inputs: HauserEQGenericSubfactorInput[]) =>
  inputs
    .filter((input) => input.nearest_distance != null)
    .sort(
      (left, right) => (left.nearest_distance ?? Infinity) - (right.nearest_distance ?? Infinity),
    )[0];

const buildGenericSubfactorBlurb = (
  details: HauserEQGenericSubfactorDetails,
  displayName: string,
  score: number,
  subfactor: string,
) => {
  const { inputs } = details;
  const riskName = displayName.replace(/ Risk$/, '').toLocaleLowerCase();
  const riskLevel = riskLabelFromScore(score);
  const qualityLevel = qualityLabelFromScore(score);
  const ratedInputs = inputs
    .filter((input) => typeof input.value === 'string' && EXPOSURE_RANK[input.value] != null)
    .sort((left, right) => EXPOSURE_RANK[String(right.value)] - EXPOSURE_RANK[String(left.value)]);
  const dominant = ratedInputs[0];

  if (subfactor === 'contamination_proximity') {
    const total = inputs.reduce((sum, input) => sum + Number(input.value || 0), 0);
    const closest = nearestInput(inputs);
    const nearestSentence = closest
      ? ` The closest returned record${
          closest.nearest_name ? `, ${closest.nearest_name},` : ''
        } is approximately ${closest.nearest_distance} miles away.`
      : '';
    return total === 0
      ? 'No nearby contamination records were identified in the available sources.'
      : `The proximity model includes ${total} potential contamination ${
          total === 1 ? 'record' : 'records'
        } across the available source types.${nearestSentence}`;
  }

  if (subfactor === 'recent_disaster_history') {
    const count = Number(inputs[0]?.value ?? 0);
    return count === 0
      ? 'No recent damaging events were identified for this area.'
      : `Available records include ${count} recent damaging ${
          count === 1 ? 'event' : 'events'
        } for this area, resulting in ${riskLevel} modeled historical risk.`;
  }

  if (subfactor === 'school_proximity') {
    const schools = inputs[0];
    const count = Number(schools?.value ?? 0);
    const nearest = schools?.nearest_distance;
    const nearestDescription =
      nearest == null
        ? ''
        : ` The nearest returned school${
            schools.nearest_name ? `, ${schools.nearest_name},` : ''
          } is approximately ${nearest} miles away.`;
    return `School proximity is ${qualityLevel}. The available data includes ${count} K–12 ${
      count === 1 ? 'school' : 'schools'
    }.${nearestDescription}`;
  }

  if (['overall_crime', 'violent_crime', 'property_crime'].includes(subfactor)) {
    const numericInputs = inputs
      .filter((input) => typeof input.value === 'number')
      .sort((left, right) => Number(right.value) - Number(left.value));
    const highest = numericInputs[0];
    const crimeScopes: Record<string, string> = {
      overall_crime: 'overall crime',
      violent_crime: 'violent crime',
      property_crime: 'property crime',
    };
    const driver = highest
      ? ` ${labelForInput(highest)} has the highest available index at ${
          highest.value
        }; 100 represents the national average.`
      : '';
    return `Modeled ${crimeScopes[subfactor]} risk is ${riskLevel} for this area.${driver}`;
  }

  if (RISK_SUBFACTORS.has(subfactor)) {
    const driver = dominant
      ? ` ${labelForInput(dominant)} is the strongest modeled contributor at ${lowerRating(
          String(dominant.value),
        )} exposure.`
      : '';
    return `Modeled ${riskName} risk is ${riskLevel} overall.${driver}`;
  }

  if (
    [
      'walkability',
      'transit_access',
      'fire_protection',
      'law_enforcement',
      'medical_response',
    ].includes(subfactor)
  ) {
    const primary = inputs[0];
    const sourceValue = primary
      ? ` The available data rates ${labelForInput(primary).toLocaleLowerCase()} as ${
          primary.value
        }.`
      : '';
    return `${displayName} is ${qualityLevel} for this location.${sourceValue}`;
  }

  if (subfactor === 'lot_size' || subfactor === 'elevation') {
    const primary = inputs[0];
    const recorded = primary ? formatGenericValue(primary.value, primary.unit) : 'not available';
    return `${displayName} is recorded as ${recorded} and is evaluated as ${qualityLevel} within the parcel model.`;
  }

  if (subfactor === 'sewer_service' || subfactor === 'water_service') {
    return `${displayName} is listed as ${
      inputs[0]?.value ?? 'not available'
    }, producing a ${qualityLevel} utility-service contribution.`;
  }

  if (subfactor === 'building_codes') {
    const adoptedCodes = inputs.map(
      (input) => `${labelForInput(input)}: ${formatGenericValue(input.value)}`,
    );
    return `Municipal building standards are ${qualityLevel} in the parcel model. Available records show ${adoptedCodes.join(
      ' and ',
    )}.`;
  }

  return `${displayName} is ${qualityLevel} for this location based on ${
    inputs.length
  } contributing ${inputs.length === 1 ? 'value' : 'values'}.`;
};

const GenericSubfactorDetails = ({
  details,
  displayName,
  score,
  subfactor,
  narrative,
}: {
  details: HauserEQGenericSubfactorDetails;
  displayName: string;
  score: number;
  subfactor: string;
  narrative?: string;
}) => {
  if (details.inputs.length === 0) return null;
  const blurb = narrative ?? buildGenericSubfactorBlurb(details, displayName, score, subfactor);
  const isContamination = subfactor === 'contamination_proximity';

  return (
    <div data-testid={`generic-details-${subfactor}`} style={{ padding: '4px 0 8px' }}>
      {blurb && (
        <div
          data-testid={`generic-blurb-${subfactor}`}
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {blurb}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isContamination
            ? 'repeat(3, minmax(0, 1fr))'
            : 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: isContamination ? 12 : 8,
          marginTop: 8,
        }}>
        {details.inputs.map((input) => (
          <div
            key={input.path}
            data-testid={isContamination ? `contamination-source-${input.key}` : undefined}
            style={{
              borderTop: '1px solid var(--tx-line)',
              paddingTop: 8,
              minWidth: 0,
              ...(isContamination
                ? {
                    display: 'grid',
                    gridTemplateRows: '32px auto 18px',
                    alignContent: 'start',
                  }
                : {}),
            }}>
            <div
              style={{
                color: 'var(--tx-muted)',
                fontSize: 11,
                ...(isContamination
                  ? {
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                    }
                  : {}),
              }}>
              {labelForInput(input)}
            </div>
            <div style={{ color: COLORS.text, fontSize: 12, marginTop: isContamination ? 0 : 3 }}>
              {formatGenericValue(input.value, input.unit)}
              {isContamination && (
                <span style={{ color: COLORS.textDim }}>
                  {' · '}
                  {input.nearest_name ?? 'No nearby records'}
                </span>
              )}
              {input.national_percentile != null && (
                <Tooltip title="Modeled national risk percentile" arrow>
                  <span style={{ color: COLORS.textDim, cursor: 'help' }}>
                    {' · '}
                    {input.national_percentile.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })}
                    %
                  </span>
                </Tooltip>
              )}
              {input.annualized_frequency != null && (
                <Tooltip title="Modeled average events per year" arrow>
                  <span style={{ color: COLORS.textDim, cursor: 'help' }}>
                    {' · '}
                    {input.annualized_frequency.toLocaleString(undefined, {
                      maximumFractionDigits: 4,
                    })}
                    /yr
                  </span>
                </Tooltip>
              )}
              {!isContamination && input.nearest_distance != null && (
                <span style={{ color: COLORS.textDim }}>
                  {' · '}
                  {input.nearest_name ? `${input.nearest_name} · ` : ''}
                  {input.nearest_distance} mi nearest
                </span>
              )}
            </div>
            {isContamination && (
              <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: '18px' }}>
                {input.nearest_distance != null ? `${input.nearest_distance} mi nearest` : '—'}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const NoiseExposureDetails = ({
  details,
  narrative,
}: {
  details: HauserEQNoiseExposureDetails;
  narrative?: string;
}) => {
  const modes = NOISE_MODE_ORDER.flatMap((key) => {
    const mode = details.modes[key];
    return mode ? [[key, mode] as const] : [];
  });
  if (modes.length === 0) return null;

  return (
    <div data-testid="noise-exposure-details" style={{ padding: '4px 0 8px' }}>
      {narrative && (
        <div
          data-testid="noise-exposure-blurb"
          style={{ color: 'var(--tx-muted)', fontSize: 12, fontWeight: 500, lineHeight: 1.55 }}>
          {narrative}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 8,
          marginTop: 8,
        }}>
        {modes.map(([key, mode]) => {
          const sound = mode?.daily_equivalent_sound_level;
          const showSound = sound && sound.value > 0;
          return (
            <div
              key={key}
              style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, minWidth: 0 }}>
              <div
                style={{
                  color: 'var(--tx-muted)',
                  fontSize: 11,
                }}>
                {NOISE_MODE_LABELS[key] ?? key}
              </div>
              <div style={{ color: 'var(--tx-text)', fontSize: 13, fontWeight: 500, marginTop: 4 }}>
                {mode?.rating ?? (mode?.grade ? `Grade ${mode.grade}` : 'Not available')}
                {showSound && (
                  <span style={{ color: COLORS.textDim }}>
                    {' · '}
                    {sound.value} {sound.unit === 'Decibels' || !sound.unit ? 'dB' : sound.unit}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ContextScoreView = () => {
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapReady, setMapReady] = useState(false);

  const mapsLib = useMapsLibrary('maps');
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (mapsLib) setGeocoder(new google.maps.Geocoder());
  }, [mapsLib]);
  const [expandedCtxCat, setExpandedCtxCat] = useState<string | null>(null);
  const [hoveredCtxCat, setHoveredCtxCat] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { scoreToGrade, gradeBands } = useScoringConfig();
  const gradeColor = (grade: string): string => gradeBands[grade]?.color ?? '#666';

  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;

  const {
    data,
    isLoading,
    error,
    contextSubscoreExplanations,
    contextSubfactorExplanations,
    contextCategoryExplanations: _contextCategoryExplanations,
    contextTabs,
    contextScoreDrivers,
    nearestPointsOfInterest,
  } = useHauserEQResults(propertyId);

  // NHD findings — scaffold reads from a stub until Treeline ships the
  // real upload + extraction pipeline. Used to (a) annotate subfactors
  // that the report influenced and (b) mount the NHD Disclosures card.
  const { data: nhdFindings } = useNHDFindings(propertyId);
  const heroFinding = useMemo(() => pickHeroFinding(nhdFindings ?? null), [nhdFindings]);

  // Geocode property address for map center
  const geocodeProperty = useCallback(() => {
    const property = selectedProperty?.property;
    // No address — show map at default center immediately
    if (!property?.address) {
      setMapReady(true);
      return;
    }
    // Geocoder not ready yet — wait for it to initialize (effect re-runs when geocoder changes)
    if (!geocoder) return;

    const query = `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`;
    geocoder.geocode({ address: query }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
        const loc = results[0].geometry.location;
        setMapCenter({ latitude: loc.lat(), longitude: loc.lng() });
      }
      setMapReady(true);
    });
  }, [selectedProperty?.property, geocoder]);

  // Reset map when property changes so it remounts with correct coordinates
  useEffect(() => {
    setMapReady(false);
  }, [propertyId]);

  useEffect(() => {
    geocodeProperty();
  }, [geocodeProperty]);

  // Build category data from API
  const ctxCategoryData = useMemo(() => {
    const rawScores = data?.context_category_scores ?? {};
    const apiScores = Object.fromEntries(
      Object.entries(rawScores).map(([k, v]) => [k.toLowerCase(), v]),
    );
    const apiDetails = Object.fromEntries(
      Object.entries(data?.context_category_details ?? {}).map(([k, v]) => [k.toLowerCase(), v]),
    );

    return Object.fromEntries(
      CONTEXT_CATEGORY_KEYS.map((key) => {
        const mappedKey = Object.entries(API_TO_CTX_CAT).find(([, v]) => v === key)?.[0];
        const apiScore = apiScores[mappedKey ?? ''] ?? apiScores[key.toLowerCase()];
        // Distinguish "no data" from a real low score. A null `score` tells
        // the renderer to show "N/A" instead of 0/100 + F.
        const score: number | null = typeof apiScore === 'number' ? Math.round(apiScore) : null;
        const grade = score != null ? scoreToGrade(score) : null;

        const apiCatDetail = apiDetails[mappedKey ?? ''] ?? apiDetails[key.toLowerCase()];
        const subfactors = apiCatDetail?.subfactors ?? {};

        return [key, { score, grade, subfactors }];
      }),
    );
  }, [data]);

  // Map PascalCase subscore keys to snake_case API driver keys
  const CTX_SUBSCORE_TO_DRIVER_KEY: Record<string, string> = {
    LocationQuality: 'location_quality',
    RiskExposure: 'risk_exposure',
  };

  // Build subscore stories from API data only
  const ctxSubscoreStories = useMemo(() => {
    const tabScores = Object.fromEntries((contextTabs ?? []).map((t) => [t.id, t.score]));
    const explanationsByPascal = Object.fromEntries(
      Object.entries(contextSubscoreExplanations ?? {}).map(([k, v]) => [
        k
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(''),
        v,
      ]),
    );

    const stories: Record<
      string,
      { question: string; score: number; narrative: string; strengths: string[]; gaps: string[] }
    > = {};
    for (const key of Object.keys(CTX_SUBSCORE_QUESTIONS).filter((k) => !isHiddenSubscore(k))) {
      const score = tabScores[key];
      const narrative = explanationsByPascal[key];
      const driverKey = CTX_SUBSCORE_TO_DRIVER_KEY[key];
      const drivers = driverKey ? contextScoreDrivers?.[driverKey] : null;

      if (score != null || narrative || drivers) {
        stories[key] = {
          question: CTX_SUBSCORE_QUESTIONS[key],
          score: score ?? 0,
          narrative: narrative ?? '',
          strengths: drivers?.driving_up ?? [],
          gaps: drivers?.driving_down ?? [],
        };
      }
    }
    return stories;
  }, [contextSubscoreExplanations, contextTabs, contextScoreDrivers]);

  const renderCtxCategoryRows = () => {
    const entries = Object.entries(ctxCategoryData);
    return (
      <div style={{ paddingBottom: 12 }}>
        {entries.map(([catKey, cat], idx) => {
          const hasScore = cat.score != null;
          const scoreBarColor = hasScore ? gradeColor(cat.grade ?? '') : COLORS.textDim;
          const isExpanded = expandedCtxCat === catKey;
          const categoryHasNhdImpact = Object.keys(cat.subfactors).some(
            (sf) => !!nhdFindings?.influencingSubfactors?.[sf],
          );
          const displayName = CTX_CATEGORY_LABELS[catKey] ?? catKey;

          return (
            <div key={catKey}>
              {idx > 0 && (
                <div
                  style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 20px' }}
                />
              )}

              <div
                data-testid={`context-category-${catKey.toLowerCase().replace(/_/g, '-')}`}
                onClick={() => setExpandedCtxCat(isExpanded ? null : catKey)}
                onMouseEnter={() => setHoveredCtxCat(catKey)}
                onMouseLeave={() => setHoveredCtxCat(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  height: 56,
                  cursor: 'pointer',
                  gap: 14,
                }}>
                <Tip
                  text={CTX_RADAR_TOOLTIPS[catKey]}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 0,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLORS.text,
                        lineHeight: 1,
                      }}>
                      {displayName}
                    </span>
                    {categoryHasNhdImpact && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: COLORS.nhdAccent,
                          background: 'var(--tx-fill-2)',
                          padding: '2px 7px',
                          borderRadius: 10,
                          letterSpacing: '0.04em',
                        }}>
                        NHD
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: COLORS.textDim, marginTop: 3 }}>
                    {Object.keys(cat.subfactors).length} subfactors
                  </span>
                </Tip>

                <span style={{ flex: 1 }} />

                {hasScore && (
                  <div
                    style={{
                      width: 44,
                      height: 3,
                      background: 'var(--tx-fill-2)',
                      borderRadius: 3,
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}>
                    <div
                      style={{
                        width: `${cat.score}%`,
                        height: '100%',
                        background: scoreBarColor,
                        borderRadius: 3,
                        opacity: hoveredCtxCat === catKey ? 1 : 0.4,
                        transition: 'opacity 0.2s',
                      }}
                    />
                  </div>
                )}

                <span
                  style={{
                    fontFamily: VALUE_FONT_SMALL,
                    fontSize: 22,
                    fontWeight: 300,
                    color: hasScore ? COLORS.text : 'var(--tx-faint)',
                    letterSpacing: '-0.04em',
                    lineHeight: 1,
                    flexShrink: 0,
                    minWidth: 44,
                    textAlign: 'right',
                  }}>
                  {hasScore ? cat.score : '–'}
                </span>

                <span
                  data-testid={`context-chevron-${catKey.toLowerCase().replace(/_/g, '-')}`}
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: COLORS.textDim,
                  }}>
                  <ChevronDown size={16} />
                </span>
              </div>

              {isExpanded && (
                <ScrollHintList
                  // the factors as flat hairline rows on the card's edge; no inner scroll, no tiles
                  style={{
                    padding: isMobile ? '4px 20px 16px' : '0 20px 12px',
                  }}>
                  {Object.entries(cat.subfactors).map(([sf, detail]) => {
                    const nhdHit = nhdFindings?.influencingSubfactors?.[sf];
                    const categoryNarratives = Object.entries(
                      contextSubfactorExplanations ?? {},
                    ).find(([key]) => key.toUpperCase() === catKey)?.[1];
                    const narrative = categoryNarratives?.[sf];
                    return (
                      // each factor's own track parts it from the next; no rules between them
                      <div key={sf}>
                        <SubfactorRow
                          label={sf}
                          score={detail.score}
                          scoreFontFamily={VALUE_FONT_SMALL}
                          scoreFontSize={15}
                          showScoreDenominator={false}
                          emptyScoreLabel="N/A"
                          nhdInfluenced={!!nhdHit}
                          nhdDetail={nhdHit?.detail}
                          details={
                            sf === 'air_quality' &&
                            detail.details &&
                            'overall' in detail.details ? (
                              <AirQualityDetails details={detail.details} narrative={narrative} />
                            ) : sf === 'water_quality' &&
                              detail.details &&
                              'components' in detail.details ? (
                              <WaterQualityDetails
                                details={detail.details as HauserEQWaterQualityDetails}
                                narrative={narrative}
                              />
                            ) : sf === 'noise_exposure' &&
                              detail.details &&
                              'modes' in detail.details ? (
                              <NoiseExposureDetails
                                details={detail.details}
                                narrative={narrative}
                              />
                            ) : sf === 'radon_mold' &&
                              detail.details &&
                              'components' in detail.details ? (
                              <RadonMoldDetails
                                details={detail.details as HauserEQRadonMoldDetails}
                                narrative={narrative}
                              />
                            ) : sf === 'overall_crime' &&
                              detail.details &&
                              'areas' in detail.details ? (
                              <CrimeOverallDetails
                                details={detail.details as HauserEQCrimeOverallDetails}
                                narrative={narrative}
                              />
                            ) : (sf === 'violent_crime' || sf === 'property_crime') &&
                              detail.details &&
                              'components' in detail.details ? (
                              <CrimeComponentDetails
                                details={detail.details as HauserEQCrimeComponentDetails}
                                narrative={narrative}
                                subfactor={sf}
                              />
                            ) : (sf === 'walkability' ||
                                sf === 'transit_access' ||
                                sf === 'fire_protection' ||
                                sf === 'law_enforcement' ||
                                sf === 'medical_response') &&
                              detail.details ? (
                              <LocationDetails
                                details={detail.details as HauserEQLocationDetails}
                                narrative={narrative}
                                subfactor={sf}
                              />
                            ) : (sf === 'school_proximity' ||
                                sf === 'childcare_proximity' ||
                                sf === 'healthcare_access' ||
                                sf === 'daily_services') &&
                              detail.details &&
                              'sources' in detail.details ? (
                              <AmenityDetails
                                details={detail.details as HauserEQAmenityDetails}
                                narrative={narrative}
                                subfactor={sf}
                              />
                            ) : detail.score != null &&
                              detail.details &&
                              'inputs' in detail.details ? (
                              <GenericSubfactorDetails
                                details={detail.details}
                                displayName={detail.display_name}
                                score={detail.score}
                                subfactor={sf}
                                narrative={narrative}
                              />
                            ) : undefined
                          }
                        />
                      </div>
                    );
                  })}
                </ScrollHintList>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Big screens (27"-class: at least 1680 wide and 1000 tall) carry the category rows in the score card; smaller
  // computers split them into the reading column (the same rule as The Home)
  const fitsCombinedCard = () => window.innerWidth >= 1680 && window.innerHeight >= 1000;
  const [combinedCard, setCombinedCard] = useState(fitsCombinedCard);
  useEffect(() => {
    const onResize = () => setCombinedCard(fitsCombinedCard());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <Box
      data-testid="context-score-view"
      sx={{
        pt: 0,
        pb: isMobile ? '10px' : '20px',
        px: isMobile ? '10px' : 0,
      }}>
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <ContextScoreCard footer={renderCtxCategoryRows()} />
          <SubscoreDetailCard
            stories={ctxSubscoreStories}
            isLoading={isLoading}
            hasData={Object.keys(ctxSubscoreStories).length > 0}
            error={!!error}
            mode="context"
            nhdFindings={nhdFindings ?? undefined}
          />
          {nhdFindings && (
            <NHDDisclosuresCard
              heroFinding={heroFinding}
              disclosures={nhdFindings.disclosures}
              taxAssessments={nhdFindings.taxAssessments}
              source={nhdFindings.source}
              extractedAt={nhdFindings.extractedAt}
            />
          )}
          <NeighborhoodMapCard
            center={mapCenter}
            propertyAddress={selectedProperty?.property.address ?? ''}
            isLoading={isLoading || !mapReady}
            nearestPointsOfInterest={nearestPointsOfInterest}
          />
        </Box>
      ) : (
        <Grid container spacing="20px">
          {/* the instrument, as on The Home: on big screens the score card carries its category rows at natural
              height; on laptops it fills the area under the pinned header, its radar carrying the subscores, and the
              rows move to the reading column. Either way it stays put while the right column scrolls */}
          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{
              minWidth: 0,
              alignSelf: 'flex-start',
              position: 'sticky',
              top: 0,
              // 27" (combined): the column is exactly the window under the header, the same on The Home and The Area
              height: combinedCard ? 'calc(100dvh - 120px)' : 'max(460px, calc(100dvh - 120px))',
              ...(combinedCard && {
                maxHeight: 'calc(100dvh - 120px)',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }),
            }}>
            {combinedCard ? <ContextScoreCard fill footer={renderCtxCategoryRows()} /> : <ContextScoreCard axisValues />}
          </Grid>
          {/* Right column — context that interprets the score. 27" (combined): at least the window under the header; the
              signals card gets room to breathe and the map takes the rest, so the column ends with the score card */}
          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{
              minWidth: 0,
              ...(combinedCard && { display: 'flex', flexDirection: 'column', minHeight: 'calc(100dvh - 120px)' }),
            }}>
            <SubscoreDetailCard
              stories={ctxSubscoreStories}
              isLoading={isLoading}
              hasData={Object.keys(ctxSubscoreStories).length > 0}
              error={!!error}
              mode="context"
              nhdFindings={nhdFindings ?? undefined}
              roomy={combinedCard}
            />
            {!combinedCard && (
              <Box
                data-testid="context-by-category"
                sx={{
                  mt: '20px',
                  p: '20px',
                  borderRadius: '24px',
                  backgroundColor: 'var(--tx-glass-panel)',
                  backgroundImage: 'var(--tx-surface-light)',
                  boxShadow: 'var(--tx-shadow)',
                }}>
                <Box sx={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)', mb: '8px' }}>
                  By category
                </Box>
                {/* the rows bring their own 12px foot (for the score card's footer); cancel it so the card keeps an even 20 */}
                <Box sx={{ mx: '-20px', mb: '-12px' }}>{renderCtxCategoryRows()}</Box>
              </Box>
            )}
            {nhdFindings && (
              <Box sx={{ mt: '20px' }}>
                <NHDDisclosuresCard
                  heroFinding={heroFinding}
                  disclosures={nhdFindings.disclosures}
                  taxAssessments={nhdFindings.taxAssessments}
                  source={nhdFindings.source}
                  extractedAt={nhdFindings.extractedAt}
                />
              </Box>
            )}
            <Box sx={{ mt: '20px', ...(combinedCard && { flex: 1, display: 'flex', flexDirection: 'column', '& > div': { flex: 1 } }) }}>
              <NeighborhoodMapCard
                center={mapCenter}
                propertyAddress={selectedProperty?.property.address ?? ''}
                isLoading={isLoading || !mapReady}
                nearestPointsOfInterest={nearestPointsOfInterest}
                fill={combinedCard}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
